// CS-1750 - Start
import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  useTheme,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, DarkModeOutlined, ErrorOutline, LightModeOutlined } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import Footer from "../../../common/Footer";
import Card from "../../../common/Card";
import { ColorModeContext, tokens } from "../../../../theme";
import { useBranding } from "../../../../BrandingContext";
import { useAppDispatch, useAppSelector } from "../../../../redux/slice/redux-hooks";
import { stripeOAuthConnectData } from "../../../../redux/slice/invoice/InvoiceSlice";

const APPBAR_H = 64;

const StripeConnectOAuthAccess: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = React.useContext(ColorModeContext);
  const location = useLocation();
  const branding = useBranding();
  const dispatch = useAppDispatch();
  const { stripeOAuthConnectStatus } = useAppSelector((state) => state.invoiceSlice);

  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const stripeError = params.get("error");
  const stripeErrorDescription = params.get("error_description");

  useEffect(() => {
    if (code) {
      dispatch(stripeOAuthConnectData({ code }));
    }
  }, [code]);

  const isLoading = stripeOAuthConnectStatus === "loading";
  const isSuccess = stripeOAuthConnectStatus === "success";
  const isError = stripeOAuthConnectStatus === "error" || !!stripeError;

  return (
    <>
      <Box>
        <AppBar position="fixed" elevation={1} sx={{ bgcolor: colors.grey[800] }}>
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: APPBAR_H },
              px: 2,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
            }}
          >
            <Box />
            <Box sx={{ justifySelf: "center", display: "flex", alignItems: "center" }}>
              <img
                alt="logo"
                src={
                  theme.palette.mode === "dark"
                    ? branding?.logoWhite
                    : branding?.logo || require("../../../../branding-assets/v3biomed/logo.png")
                }
                style={{ height: "80px", objectFit: "contain", maxWidth: "100%" }}
              />
            </Box>
            <Box sx={{ justifySelf: "end" }}>
              <IconButton onClick={colorMode.toggleColorMode}>
                {theme.palette.mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Toolbar sx={{ minHeight: { xs: 56, sm: APPBAR_H } }} />

        <Container
          maxWidth="sm"
          sx={{
            minHeight: `calc(100dvh - ${APPBAR_H}px)`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <Card
            sx={{
              width: "100%",
              p: 4,
              border: "1px solid",
              borderColor: isError
                ? colors.error[300]
                : isSuccess
                ? colors.success[300]
                : colors.grey[600],
              borderRadius: "12px",
              backgroundColor: colors.grey[800],
              textAlign: "center",
            }}
          >
            {/* Loading */}
            {isLoading && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <CircularProgress size={48} />
                <Typography variant="h6">Processing your Stripe authorization...</Typography>
                <Typography variant="body2" color={colors.grey[400]}>
                  Please wait while we complete your onboarding.
                </Typography>
              </Box>
            )}

            {/* Success */}
            {isSuccess && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <CheckCircle sx={{ color: colors.success[300], fontSize: 56 }} />
                <Typography variant="h5" fontWeight="bold">Onboarding completed</Typography>
                <Typography variant="body2" color={colors.grey[400]}>
                  Your Stripe account has been successfully connected.
                </Typography>
              </Box>
            )}

            {/* Error */}
            {isError && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <ErrorOutline sx={{ color: colors.error[300], fontSize: 56 }} />
                <Typography variant="h5" fontWeight="bold">Connection Failed</Typography>
                <Typography variant="body2" color={colors.error[300]}>
                  {stripeErrorDescription || stripeError || "An error occurred while connecting your Stripe account."}
                </Typography>
              </Box>
            )}

            {/* No params */}
            {!isLoading && !isSuccess && !isError && !code && (
              <Typography variant="body2" color={colors.grey[500]}>
                No OAuth callback parameters found in the URL.
              </Typography>
            )}
          </Card>
        </Container>

        <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default StripeConnectOAuthAccess;
// CS-1750 - End
